import { api } from "#/lib/api-client";
import type { AuditActorOption, AuditEventPageResponse, AuditSearchFilters } from "./types";

export const auditApi = {
  async search(filters: AuditSearchFilters, page: number, size: number): Promise<AuditEventPageResponse> {
    const { data } = await api.get<AuditEventPageResponse>("/api/hr/audit/events", {
      params: {
        module: filters.module || undefined,
        action: filters.action || undefined,
        entityType: filters.entityType || undefined,
        entityId: filters.entityId || undefined,
        actorUserId: filters.actorUserId || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
        page,
        size,
      },
    });
    return data;
  },

  /**
   * Pages through every event matching the given filters (server caps a single page at 200),
   * for report generation. Capped at `maxRows` as a sane ceiling so a very broad filter (or no
   * filter at all) can't pull an unbounded number of rows into the browser; the caller is told
   * via the returned `truncated` flag when the cap was hit so it can warn the person exporting.
   */
  async searchAll(filters: AuditSearchFilters, maxRows = 5000): Promise<{ events: AuditEventPageResponse["content"]; truncated: boolean }> {
    const size = 200;
    let page = 0;
    let events: AuditEventPageResponse["content"] = [];
    let truncated = false;
    while (true) {
      const res = await this.search(filters, page, size);
      events = events.concat(res.content);
      if (events.length >= maxRows) {
        truncated = events.length < res.totalElements;
        events = events.slice(0, maxRows);
        break;
      }
      if (res.last || res.content.length === 0) break;
      page += 1;
    }
    return { events, truncated };
  },

  /** Every distinct entityType string ever recorded — powers the entity-type autocomplete. */
  async entityTypes(): Promise<string[]> {
    const { data } = await api.get<string[]>("/api/hr/audit/events/entity-types");
    return data;
  },

  /** Up to 15 users matching the search term by name, username, or email. */
  async searchActors(q: string): Promise<AuditActorOption[]> {
    // No minimum length here: a blank query returns a default browsable list of authenticated
    // users from the backend, and every keystroke after that re-filters in real time.
    const { data } = await api.get<AuditActorOption[]>("/api/hr/audit/actors", { params: { q: q ?? "" } });
    return data;
  },
};
